import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { PrSapValidationChecklist, PrSapDetails } from '@/types/domain/pr-management';
import { CheckCircle2, XCircle, AlertTriangle, RefreshCw, ShieldCheck, Database } from 'lucide-react';

interface PrSapValidationPanelProps {
  validation: PrSapValidationChecklist;
  sapDetails: PrSapDetails;
  onRetrySap?: () => void;
  isRetrying?: boolean;
}

export const PrSapValidationPanel: React.FC<PrSapValidationPanelProps> = ({
  validation,
  sapDetails,
  onRetrySap,
  isRetrying = false,
}) => {
  const items = [
    { label: 'Company Code (1000)', passed: validation.companyCode },
    { label: 'Plant Assignment', passed: validation.plant },
    { label: 'Material Master', passed: validation.material },
    { label: 'Cost Center Hierarchy', passed: validation.costCenter },
    { label: 'GL Account Assignment', passed: validation.glAccount },
    { label: 'Purchasing Group (001)', passed: validation.purchasingGroup },
    { label: 'Currency (INR)', passed: validation.currency },
    { label: 'Quantity & UOM', passed: validation.quantity },
    { label: 'Delivery Schedule', passed: validation.deliveryDate },
    { label: 'Department Budget', passed: validation.budget },
  ];

  return (
    <Card className="border-border rounded-2xl shadow-2xs font-heading">
      <CardHeader className="pb-3 border-b border-border/60 flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-sm font-bold text-foreground flex items-center gap-2">
          <Database className="h-4 w-4 text-primary" />
          SAP S/4HANA BAPI Validation & Document Integration
        </CardTitle>

        {sapDetails.sapPrNumber ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono font-extrabold bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-500/20">
            <ShieldCheck className="h-3.5 w-3.5" /> SAP PR: {sapDetails.sapPrNumber}
          </span>
        ) : !validation.isValid ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-rose-500/10 text-rose-700 dark:text-rose-300 border border-rose-500/20">
            <AlertTriangle className="h-3.5 w-3.5" /> Validation Exception
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20">
            <CheckCircle2 className="h-3.5 w-3.5" /> Validated for SAP
          </span>
        )}
      </CardHeader>

      <CardContent className="pt-4 space-y-4">
        {/* Error Callout Box */}
        {!validation.isValid && validation.errorMsg && (
          <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 space-y-2 text-xs">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2 text-rose-700 dark:text-rose-300 font-extrabold">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>{validation.errorMsg}</span>
              </div>

              {onRetrySap && (
                <Button
                  size="sm"
                  disabled={isRetrying}
                  onClick={onRetrySap}
                  className="rounded-xl font-bold bg-rose-600 hover:bg-rose-700 text-white shrink-0"
                >
                  <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isRetrying ? 'animate-spin' : ''}`} />
                  {isRetrying ? 'Re-validating...' : 'Re-map & Retry SAP'}
                </Button>
              )}
            </div>

            {validation.resolutionGuidance && (
              <p className="text-[11px] text-rose-600 dark:text-rose-400 font-medium pl-6">
                <strong>Resolution Guidance:</strong> {validation.resolutionGuidance}
              </p>
            )}
          </div>
        )}

        {/* Validation Checklist Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
          {items.map((it) => (
            <div
              key={it.label}
              className={`p-2.5 rounded-xl border text-xs font-bold flex items-center justify-between ${
                it.passed
                  ? 'border-emerald-500/20 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300'
                  : 'border-rose-500/20 bg-rose-500/5 text-rose-700 dark:text-rose-300'
              }`}
            >
              <span className="truncate mr-1 text-[11px]">{it.label}</span>
              {it.passed ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> : <XCircle className="h-3.5 w-3.5 shrink-0" />}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
